import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import type { MetricsSnapshot } from '../lib/api';
import { formatRate, intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import {
  AXIS_TICK,
  FAILURE_COLORS,
  GRID_STROKE,
  LEGEND_STYLE,
  SERIES_FALLBACK,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_STYLE,
} from './chartTheme';

interface Props {
  history: MetricsSnapshot[];
}

export function FailureRateChart({ history }: Props) {
  const { t, locale } = useLocale();

  if (history.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
        {t('charts.noHistory')}
      </div>
    );
  }

  const queueNames = Object.keys(history[0]?.queues ?? {});

  const data = history.map((snap) => ({
    time: format(new Date(snap.timestamp), 'HH:mm'),
    ...Object.fromEntries(
      queueNames.map((q) => [
        q,
        +(snap.queues[q]?.failureRate ?? 0).toFixed(1),
      ]),
    ),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          dataKey="time"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          unit="%"
          domain={[0, 'auto']}
          // As in `ThroughputChart`: recharts' own number rendering is locale-blind.
          tickFormatter={(value) =>
            Number(value).toLocaleString(intlLocale(locale))
          }
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value) => [
            t('common.percentValue', {
              rate: formatRate(Number(value), locale),
            }),
          ]}
        />
        <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" iconSize={8} />
        {queueNames.map((q) => (
          <Line
            key={q}
            type="monotone"
            dataKey={q}
            stroke={FAILURE_COLORS[q] ?? SERIES_FALLBACK}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
